import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface CreateLottoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (date: Date, maxSlots: number) => void;
}

const CreateLottoModal: React.FC<CreateLottoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [date, setDate] = useState<string>('');
  const [maxSlots, setMaxSlots] = useState<string>('2');

  const handleSubmit = () => {
    if (!date) {
      toast.error('추첨 날짜를 선택해주세요!');
      return;
    }

    const slotsNum = parseInt(maxSlots);
    if (isNaN(slotsNum) || slotsNum < 1) {
      toast.error('휴무 가능 인원은 1명 이상이어야 합니다!');
      return;
    }
    if (slotsNum > 10) {
      toast.error('휴무 가능 인원은 최대 10명까지 가능합니다!');
      return;
    }

    const selectedDate = new Date(date);
    onSubmit(selectedDate, slotsNum);
    
    // 초기화
    setDate('');
    setMaxSlots('2');
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-2xl font-bold text-primary mb-4 text-center"
                >
                  🎲 새 로또 추첨 생성
                </Dialog.Title>

                <div className="mt-4">
                  {/* 안내 메시지 */}
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3 mb-4">
                    <div className="text-sm text-gray-700">
                      <div className="font-bold mb-1">💡 로또 추첨이란?</div>
                      <div>- 인기 날짜에 여러 명이 신청하면 <span className="text-accent font-bold">공정한 추첨</span>으로 결정!</div>
                      <div>- 관리자가 날짜와 휴무 가능 인원을 설정하세요</div>
                    </div>
                  </div>

                  {/* 추첨 날짜 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      추첨 날짜 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-accent text-base"
                      min={format(new Date(), 'yyyy-MM-dd')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      이 날짜에 쉬고 싶은 사람들이 신청할 거조씨
                    </p>
                  </div>

                  {/* 휴무 가능 인원 */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      휴무 가능 인원 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={maxSlots}
                      onChange={(e) => setMaxSlots(e.target.value)}
                      min="1"
                      max="10"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-accent text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      최대 {maxSlots}명까지 당첨될 수 있조씨 (1~10명)
                    </p>
                  </div>

                  {/* 미리보기 */}
                  {date && (
                    <div className="bg-base-cream rounded-lg p-4 mb-4">
                      <p className="text-sm text-gray-600 mb-2">📋 추첨 미리보기</p>
                      <div className="text-center">
                        <p className="text-xl font-bold text-primary mb-2">
                          {format(new Date(date), 'yyyy년 M월 d일 (EEEE)', { locale: ko })}
                        </p>
                        <div className="flex items-center justify-center gap-2">
                          <div className="bg-accent text-white px-4 py-2 rounded-full">
                            <p className="text-sm">휴무 가능</p>
                            <p className="text-2xl font-bold">{maxSlots}명</p>
                          </div>
                          <span className="text-2xl">🎲</span>
                          <div className="bg-primary text-white px-4 py-2 rounded-full">
                            <p className="text-sm">추첨 대기</p>
                            <p className="text-2xl font-bold">0명</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 경고 메시지 */}
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="text-sm text-gray-700">
                      <div className="font-bold mb-1">⚠️ 주의사항:</div>
                      <div>- 추첨 생성 후 <span className="font-bold">날짜 변경 불가</span></div>
                      <div>- 신청자가 모이면 <span className="font-bold">추첨 시작</span> 버튼으로 진행</div>
                      <div>- 추첨 완료 후 <span className="font-bold">취소 불가</span></div>
                    </div>
                  </div>
                </div>

                {/* 버튼 */}
                <div className="mt-6 flex space-x-3">
                  <button
                    type="button"
                    className="flex-1 btn-azocsii-outline py-3"
                    onClick={onClose}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="flex-1 btn-azocsii py-3"
                    onClick={handleSubmit}
                  >
                    생성하기
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateLottoModal;
